// src/pages/OnlineTests/TestSolutions/TestSolutions.tsx
import React, { useEffect, useState, useMemo } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { fetchTestSolutions } from "../../../services/api";
import "./TestSolutions.css";
import Logo          from "../../../acade_logo1.png";
import { formatSlugUpperCase } from "../../../utils/slugify";

/* ─── Types ──────────────────────────────────────────────── */
interface Solution {
  question_id:     string;
  question:        string;
  optionA:         string;
  optionB:         string;
  optionC:         string;
  optionD:         string;
  answer:          string;
  answer_desc:     string;
  selected_option?: string;
  answered:        boolean;
  marked_for_review: boolean;
  visited:         boolean;
  time_taken:      number;
  is_correct:      boolean | null;
  section_name?:   string;   // populated for multi-section tests
}

interface SectionGroup {
  section_name: string;
  solutions:    Solution[];
  startIdx:     number;      // global index where this section starts
}

interface QStatus {
  answered:          boolean;
  marked_for_review: boolean;
  visited:           boolean;
  is_correct?:       boolean | null;
}

/* ─── Helpers ────────────────────────────────────────────── */
const formatTime = (seconds: any) => {
  if (!seconds || seconds < 60) return `${seconds || 0}s`;
  const m = Math.floor(seconds / 60), s = seconds % 60;
  return `${m}m ${s}s`;
};

/* ── Palette button colours for solutions page ── */
const paletteClass = (st: QStatus, isCurrent: boolean): string => {
  let cls = "sol-btn";
  if      (st.is_correct === true)  cls += " sol-correct";
  else if (st.is_correct === false) cls += " sol-incorrect";
  else if (st.visited)              cls += " sol-visited";
  if (isCurrent)                    cls += " sol-current";
  return cls;
};

/* ════════════════════════════════════════════════════════ */
const TestSolutions = () => {
  const { slug, test_id } = useParams();
  const navigate           = useNavigate();

  const [solutions,         setSolutions]         = useState<Solution[]>([]);
  const [sections,          setSections]          = useState<SectionGroup[]>([]);
  const [isMulti,           setIsMulti]           = useState(false);
  const [currentQIdx,       setCurrentQIdx]       = useState(0);
  const [username,          setUsername]          = useState("");
  const [questionMarks,     setQuestionMarks]     = useState(0);
  const [negativeMarks,     setNegativeMarks]     = useState(0);
  const [testName,          setTestName]          = useState("");
  const [currentSectionIdx, setCurrentSectionIdx] = useState(0);

  /* ── Fetch ── */
  useEffect(() => {
    if (!test_id) return;
    (async () => {
      const user = JSON.parse(localStorage.getItem("user") || "null");
      if (!user) { alert("Please login"); return; }

      const res = await fetchTestSolutions(user.id, test_id);
      const sols: Solution[] = res.data.solutions;

      setSolutions(sols);
      setUsername(user.firstname);
      setQuestionMarks(res.data.question_marks);
      setNegativeMarks(res.data.negative_marks);
      setTestName(res.data.test_name);

      /* ── Group by section_name ── */
      const hasMulti = sols.some(s => !!s.section_name);
      setIsMulti(hasMulti);

      if (hasMulti) {
        // Preserve order; group consecutive same-section questions
        const groups: SectionGroup[] = [];
        let idx = 0;
        for (const sol of sols) {
          const name = sol.section_name || "General";
          const last = groups[groups.length - 1];
          if (!last || last.section_name !== name) {
            groups.push({ section_name: name, solutions: [sol], startIdx: idx });
          } else {
            last.solutions.push(sol);
          }
          idx++;
        }
        setSections(groups);
      } else {
        // Single section
        setSections([{
          section_name: res.data.test_name || "Section",
          solutions:    sols,
          startIdx:     0,
        }]);
      }
    })();
  }, [test_id]);

  /* ── Sync current section when global index changes ── */
  useEffect(() => {
    for (let i = sections.length - 1; i >= 0; i--) {
      if (currentQIdx >= sections[i].startIdx) {
        setCurrentSectionIdx(i);
        break;
      }
    }
  }, [currentQIdx, sections]);

  /* ── Derived ── */
  const currentQuestion  = solutions[currentQIdx];
  //const activeSection    = sections[currentSectionIdx];

  /* ── Stats ── */
  const questionStatus = useMemo<{ [qid: string]: QStatus }>(() =>
    solutions.reduce((acc, q) => {
      acc[q.question_id] = {
        answered:          !!q.selected_option,
        marked_for_review: q.marked_for_review,
        visited:           q.visited,
        is_correct:        q.is_correct,
      };
      return acc;
    }, {} as { [key: string]: QStatus }),
  [solutions]);

  const stats = useMemo(() => {
    const arr = Object.values(questionStatus);
    return {
      correctCount:     arr.filter(q => q.is_correct === true).length,
      incorrectCount:   arr.filter(q => q.is_correct === false).length,
      notAnsweredCount: arr.filter(q => !q.answered).length,
      notVisitedCount:  arr.filter(q => !q.visited).length,
    };
  }, [questionStatus]);

  /* ── Navigation ── */
  const handleNext = () => {
    if (currentQIdx < solutions.length - 1) setCurrentQIdx(p => p + 1);
  };
  const handlePrevious = () => {
    if (currentQIdx > 0) setCurrentQIdx(p => p - 1);
  };
  const handlePaletteClick = (globalIdx: number) => setCurrentQIdx(globalIdx);

  const handleSectionTabClick = (sIdx: number) => {
    setCurrentQIdx(sections[sIdx].startIdx);
  };

  /* ── Status badge ── */
  const renderStatusBadge = () => {
    if (!currentQuestion) return null;
    if (currentQuestion.is_correct === true) return (
      <>
        <span className="sol-badge sol-badge-correct">✓ Correct</span>
        <span className="sol-badge sol-badge-marks">+{questionMarks} Marks</span>
        <span className="sol-badge sol-badge-time">⏱ {formatTime(currentQuestion.time_taken)}</span>
      </>
    );
    if (currentQuestion.is_correct === false) return (
      <>
        <span className="sol-badge sol-badge-wrong">✗ Incorrect</span>
        <span className="sol-badge sol-badge-neg">−{negativeMarks} Marks</span>
        <span className="sol-badge sol-badge-time">⏱ {formatTime(currentQuestion.time_taken)}</span>
      </>
    );
    return (
      <>
        <span className="sol-badge sol-badge-skip">— Skipped</span>
        <span className="sol-badge sol-badge-zero">0 Marks</span>
        <span className="sol-badge sol-badge-time">⏱ {formatTime(currentQuestion.time_taken)}</span>
      </>
    );
  };

  /* ── Shared palette ── */
  const renderPalette = () => (
    <div className={`sol-palette-wrap${isMulti ? " sol-palette-multi" : ""}`}>
      {sections.map((sec, sIdx) => (
        <div key={sIdx} className="sol-pal-section">
          {/* Section header in palette */}
          {(isMulti || sections.length > 1) && (
            <div className="sol-pal-section-hdr">
              <span className="sol-pal-sec-name">{sec.section_name}</span>
              <span className="sol-pal-sec-count">{sec.solutions.length}Q</span>
            </div>
          )}
          <div className={`sol-pal-grid${isMulti ? " sol-pal-grid-multi" : ""}`}>
            {sec.solutions.map((sol, localIdx) => {
              const globalIdx = sec.startIdx + localIdx;
              const st        = questionStatus[sol.question_id] || {};
              return (
                <button
                  key={sol.question_id}
                  className={paletteClass(st as QStatus, currentQIdx === globalIdx)}
                  onClick={() => handlePaletteClick(globalIdx)}
                  title={`Q${globalIdx + 1}${isMulti ? ` — ${sec.section_name}` : ""}`}
                >
                  {globalIdx + 1}
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );

  /* ── Shared legend ── */
  const renderLegend = () => (
    <div className="status-legend">
      <div className="status-item"><div className="status-circle correct">{stats.correctCount}</div> Correct</div>
      <div className="status-item"><div className="status-circle incorrect">{stats.incorrectCount}</div> Incorrect</div>
      <div className="status-item"><div className="status-circle unattempted">{stats.notAnsweredCount}</div> Not Answered</div>
      <div className="status-item"><div className="status-circle not-visited">{stats.notVisitedCount}</div> Not Visited</div>
    </div>
  );

  if (!currentQuestion) return <div className="sol-loading">Loading solutions…</div>;

  /* ════════════════════════════════════════════
     RENDER
  ════════════════════════════════════════════ */
  return (
    <div className="test-page-container">

      {/* TOP HEADER */}
      <div className="test-header">
        <div className="test-header-left">
          <Link to="/"><img src={Logo} alt="logo" /></Link>
          <span className="test-name-label"> — {formatSlugUpperCase(slug || "")}</span>
        </div>
        <div className="test-header-right">
          <button className="header-btn" onClick={() => navigate(`/online-tests/test-result/${slug}/${test_id}`)}>
            ← Back to Result
          </button>
        </div>
      </div>

      {/* SECTION TABS — multi only */}
      {isMulti && sections.length > 1 && (
        <div className="section-tabs-bar">
          {sections.map((sec, sIdx) => (
            <button
              key={sIdx}
              className={`section-tab${sIdx === currentSectionIdx ? " active" : ""}`}
              onClick={() => handleSectionTabClick(sIdx)}
            >
              {sec.section_name}
              <span className="tab-count">{sec.solutions.length}Q</span>
            </button>
          ))}
        </div>
      )}

      <div className="test-body">

        {/* ══ LEFT PANEL ══ */}
        <div className="left-panel">

          {/* Section header — single only */}
        
            <div className="sections-header">SECTIONS: {testName || "…"}</div>
       

          {/* Question header */}
          <div className="question-header sol-q-header">
            <span>Question No. {currentQIdx + 1}</span>
            <span className="sol-badges">{renderStatusBadge()}</span>
          </div>

          {/* Question + options + description */}
          <div className="question-area">
            <div className="question-panel-container">
              <div
                className="question-description"
                dangerouslySetInnerHTML={{ __html: currentQuestion.question }}
              />

              <div className="options-group">
                {(["A","B","C","D"] as const).map(opt => {
                  const key   = `option${opt}` as keyof Solution;
                  const value = currentQuestion[key] as string;
                  if (!value) return null;

                  const isCorrect  = currentQuestion.answer === opt;
                  const isSelected = currentQuestion.selected_option === opt;

                  let cls = "option";
                  if (isCorrect)                cls += " correct";
                  if (isSelected && !isCorrect) cls += " incorrect";

                  return (
                    <div key={opt} className={cls}>
                      <span className="opt-label">{opt}.</span>
                      <span dangerouslySetInnerHTML={{ __html: value }} />
                    </div>
                  );
                })}
              </div>

              <hr />
              <div className="desc">
                <strong>Explanation:</strong>
                <div
                  className="answer-description"
                  dangerouslySetInnerHTML={{ __html: currentQuestion.answer_desc }}
                />
              </div>
            </div>
          </div>

          {/* Mobile sidebar */}
          <div className="mobile-sidebar">
            <div className="user-section">
              <div className="avatar">👤</div>
              <span>{username}</span>
            </div>
            <hr />
            {renderLegend()}
            <hr />
            {renderPalette()}
          </div>

          {/* Bottom controls */}
          <div className="bottom-controls">
            <button onClick={handlePrevious} disabled={currentQIdx === 0}>Previous</button>
            <button onClick={handleNext}     disabled={currentQIdx === solutions.length - 1}>Next</button>
          </div>
        </div>

        {/* ══ DESKTOP SIDEBAR ══ */}
        <div className="sidebar">
          <div className="user-section">
            <div className="avatar">👤</div>
            <span>{username}</span>
          </div>
          <hr />
          {renderLegend()}
          <hr />
          <div className="sidebar-palette-wrap">
            {renderPalette()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TestSolutions;